import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Download, LogOut, Search, Loader2, Trash2 } from "lucide-react";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const tk = sessionStorage.getItem("mirai-admin-token");
  return tk ? { Authorization: `Bearer ${tk}` } : {};
};
const fmtErr = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg).join(" ") : "Something went wrong.");

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      sessionStorage.setItem("mirai-admin-token", data.token);
      onLogin(data);
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const cls = "mt-2 w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-base focus:border-ink focus:outline-none focus:ring-4 focus:ring-sun/40";
  return (
    <div className="flex min-h-[70vh] items-center justify-center px-5 py-16">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl border border-ink/10 bg-white p-8 shadow-xl" data-testid="admin-login-form">
        <Logo testId="admin-logo" />
        <h1 className="mt-6 font-display text-2xl font-extrabold text-ink">Admin login</h1>
        <label className="mt-6 block font-display text-sm font-bold text-ink">Email
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={cls} required data-testid="admin-login-email-input" />
        </label>
        <label className="mt-4 block font-display text-sm font-bold text-ink">Password
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={cls} required data-testid="admin-login-password-input" />
        </label>
        {error && <p className="mt-4 text-sm font-medium text-red-700" role="alert" data-testid="admin-login-error">{error}</p>}
        <button disabled={busy} className="mt-6 w-full rounded-full bg-ink py-3.5 font-display font-extrabold text-white hover:bg-night disabled:opacity-70" data-testid="admin-login-submit-button">
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
};

const Dashboard = ({ user, onLogout }) => {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, { withCredentials: true, headers: authHeaders() });
      setLeads(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    } finally { setLoading(false); }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return leads;
    return leads.filter((l) => [l.name, l.mobile, l.city, l.programme, l.email].some((x) => (x || "").toLowerCase().includes(s)));
  }, [leads, q]);

  const remove = async (l) => {
    if (!window.confirm(`Delete the enquiry from ${l.name}? This cannot be undone.`)) return;
    await axios.delete(`${API}/admin/leads/${l.id}`, { withCredentials: true, headers: authHeaders() });
    setLeads((ls) => ls.filter((x) => x.id !== l.id));
  };

  const exportCsv = async () => {
    const res = await axios.get(`${API}/admin/leads/export`, { withCredentials: true, headers: authHeaders(), responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `mirai-enquiries-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8" data-testid="admin-dashboard">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-ink">Signed in as {user.email}</p>
          <h1 className="mt-2 font-display text-3xl font-extrabold text-ink">Enquiries <span className="text-night/50" data-testid="admin-leads-count">({leads.length})</span></h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <label className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-night/50" aria-hidden="true" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, mobile, city…" aria-label="Search enquiries"
              className="rounded-full border border-ink/15 bg-white py-2.5 pl-9 pr-4 text-sm focus:border-ink focus:outline-none focus:ring-4 focus:ring-sun/40" data-testid="admin-search-input" />
          </label>
          <button onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full bg-sun px-5 py-2.5 font-display text-sm font-extrabold text-night hover:bg-[#ffb63d]" data-testid="admin-export-csv-button">
            <Download className="h-4 w-4" aria-hidden="true" />Export CSV
          </button>
          <button onClick={onLogout} className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 font-display text-sm font-bold text-ink hover:bg-sky" data-testid="admin-logout-button">
            <LogOut className="h-4 w-4" aria-hidden="true" />Log out
          </button>
        </div>
      </div>
      <div className="mt-8 overflow-x-auto rounded-2xl border border-ink/10 bg-white">
        <table className="w-full min-w-[1300px] text-left text-sm" data-testid="admin-enquiries-table">
          <thead className="bg-sky font-display text-xs font-bold uppercase tracking-wider text-ink">
            <tr>{["Date", "Name", "Mobile", "Email", "City", "Qualification", "Programme", "Message", "Consent (contact / notice / time / IP)", "Requests", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="px-4 py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-ink" /></td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={11} className="px-4 py-10 text-center text-night/70" data-testid="admin-empty-state">No enquiries yet.</td></tr>}
            {filtered.map((l) => (
              <tr key={l.id} className="border-t border-ink/5 align-top text-night" data-testid={`admin-lead-row-${l.id}`}>
                <td className="whitespace-nowrap px-4 py-3">{new Date(l.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</td>
                <td className="px-4 py-3 font-semibold">{l.name}</td>
                <td className="whitespace-nowrap px-4 py-3"><a className="text-ink underline" href={`https://wa.me/91${l.mobile}`} target="_blank" rel="noopener noreferrer">{l.mobile}</a></td>
                <td className="px-4 py-3">{l.email || "—"}</td>
                <td className="px-4 py-3">{l.city}</td>
                <td className="px-4 py-3">{l.qualification}</td>
                <td className="px-4 py-3">{l.programme}</td>
                <td className="max-w-xs px-4 py-3 text-night/80">{l.message || "—"}</td>
                <td className="px-4 py-3 text-xs">{l.consent ? `${l.consent.contact ? "Contact OK" : "No contact"} · ${l.consent.notice_version} · ${new Date(l.consent.timestamp).toLocaleString("en-IN")} · ${l.consent.ip}` : "—"}</td>
                <td className="px-4 py-3 text-xs font-semibold text-red-700">{[l.contact_withdrawn && "Do not contact", l.erasure_requested && "Erase requested"].filter(Boolean).join(" · ") || "—"}</td>
                <td className="px-4 py-3"><button onClick={() => remove(l)} aria-label={`Delete enquiry from ${l.name}`} className="rounded-full p-2 text-red-700 hover:bg-red-50" data-testid={`admin-delete-lead-${l.id}`}><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Institutes onLogout={onLogout} />
    </div>
  );
};

const Institutes = ({ onLogout }) => {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/institutes`, { withCredentials: true, headers: authHeaders() });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { if (e.response?.status === 401) onLogout(); }
  }, [onLogout]);
  useEffect(() => { load(); }, [load]);
  const save = async (r) => {
    const { id, ...body } = r;
    try { await axios.put(`${API}/admin/institutes/${id}`, body, { withCredentials: true, headers: authHeaders() }); load(); }
    catch (e) { window.alert(fmtErr(e.response?.data?.detail)); }
  };
  const add = async () => {
    if (name.trim().length < 2) return;
    await axios.post(`${API}/admin/institutes`, { name: name.trim(), order: rows.length }, { withCredentials: true, headers: authHeaders() });
    setName(""); load();
  };
  const del = async (r) => {
    if (!window.confirm(`Remove ${r.name}?`)) return;
    await axios.delete(`${API}/admin/institutes/${r.id}`, { withCredentials: true, headers: authHeaders() }); load();
  };
  const edit = (id, k, v) => setRows((rs) => rs.map((x) => (x.id === id ? { ...x, [k]: v } : x)));
  const cls = "rounded-lg border border-ink/20 px-2 py-1.5 text-sm";
  return (
    <section className="mt-14" data-testid="admin-institutes">
      <h2 className="font-display text-2xl font-extrabold text-ink">Universities we counsel for</h2>
      <p className="mt-2 max-w-3xl text-sm text-night">Every row here appears on the website as a plain-text name card. Use “Confirmed in writing” to record which universities have confirmed your arrangement in writing (for your records). Add the official website only if the university supplied or confirmed it.</p>
      <div className="mt-4 flex gap-2"><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Add university name" className={cls} data-testid="admin-institute-name-input" /><button onClick={add} className="rounded-full bg-ink px-4 py-2 text-sm font-bold text-white" data-testid="admin-institute-add-button">Add</button></div>
      <div className="mt-4 overflow-x-auto rounded-2xl border border-ink/10 bg-white">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="bg-sky text-xs font-bold uppercase text-ink"><tr>{["Name", "Confirmed in writing", "Last verified on UGC-DEB list", "Official website (https://)", "", ""].map((h, i) => <th key={i} className="px-3 py-3">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-ink/5" data-testid={`admin-institute-row-${r.id}`}>
                <td className="px-3 py-2"><input value={r.name} onChange={(e) => edit(r.id, "name", e.target.value)} className={cls} aria-label="Name" /></td>
                <td className="px-3 py-2"><input type="checkbox" checked={r.confirmed_in_writing} onChange={(e) => edit(r.id, "confirmed_in_writing", e.target.checked)} className="h-5 w-5" aria-label={`Confirmed in writing: ${r.name}`} data-testid={`admin-institute-confirmed-${r.id}`} /></td>
                <td className="px-3 py-2"><input type="date" value={r.last_verified_deb || ""} onChange={(e) => edit(r.id, "last_verified_deb", e.target.value || null)} className={cls} aria-label="Last verified date" /></td>
                <td className="px-3 py-2"><input value={r.official_url || ""} onChange={(e) => edit(r.id, "official_url", e.target.value || null)} className={`${cls} w-64`} aria-label="Official website" /></td>
                <td className="px-3 py-2"><button onClick={() => save(r)} className="rounded-full bg-sun px-3 py-1.5 text-xs font-bold text-night" data-testid={`admin-institute-save-${r.id}`}>Save</button></td>
                <td className="px-3 py-2"><button onClick={() => del(r)} className="text-xs font-bold text-red-700">Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    document.title = "Admin | Mirai Educations";
    axios.get(`${API}/auth/me`, { withCredentials: true, headers: authHeaders() })
      .then((r) => setUser(r.data)).catch(() => setUser(null)).finally(() => setChecked(true));
  }, []);

  const logout = useCallback(async () => {
    sessionStorage.removeItem("mirai-admin-token");
    setUser(null);
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch { /* ignore */ }
  }, []);

  if (!checked) return <div className="py-32 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-ink" /></div>;
  return user ? <Dashboard user={user} onLogout={logout} /> : <Login onLogin={setUser} />;
}
